import { BrowserWebSocketClientAdapter, JoinMessage } from "@automerge/automerge-repo-network-websocket";
import {
    type PeerId,
    type PeerMetadata,
  } from "@automerge/automerge-repo"
import { App } from "vue";


export class FoliaBrowserWebsocketClientAdaptor extends BrowserWebSocketClientAdapter {

    $fetch: any;
    $auth0: any;
    connectionIssueNoticed: boolean = false;
    baseUrl: string = ""
    private _isRefreshingToken: boolean = false;
    private _heartbeatInterval?: ReturnType<typeof setInterval>
    // 30s — well under the typical 60s proxy idle-timeout that causes 1006 drops
    private readonly HEARTBEAT_MS = 30_000

    constructor(url: string, app: App) {
        super(url)
        this.baseUrl = url
        this.$fetch = app.config.globalProperties.$fetch;
        this.$auth0 = app.config.globalProperties.$auth0;
    }

    connect(peerId: PeerId, peerMetadata: PeerMetadata) {
        console.log("Connecting automerge repo to sync server.....")

        // The parent only removes listeners from the old socket but never calls
        // .close() on it, which leaves CONNECTING/OPEN sockets dangling on the
        // server. Strip listeners first (so the close event doesn't re-trigger
        // onClose), then explicitly close before super creates the new socket.
        if (this.socket &&
            this.socket.readyState !== WebSocket.CLOSED &&
            this.socket.readyState !== WebSocket.CLOSING) {
            this.socket.removeEventListener("open", this.onOpen)
            this.socket.removeEventListener("close", this.onClose)
            this.socket.removeEventListener("message", this.onMessage)
            this.socket.close()
        }

        this.url = this.baseUrl + "?Authentication=" + localStorage.getItem("accessToken")
        super.connect(peerId, peerMetadata);
    }

    /**
     * Refreshes the Auth0 token (updates localStorage) before reconnecting.
     * Used on close/reconnect so an expired token doesn't cause an infinite
     * loop of rejected WebSocket connections.
     */
    private async connectWithFreshToken(peerId: PeerId, peerMetadata: PeerMetadata) {
        if (this._isRefreshingToken) return;
        this._isRefreshingToken = true;
        try {
            await this.$auth0.getUser(); // calls getTokenSilently() → updates localStorage
        } catch (e) {
            console.warn("Automerge: token refresh failed before reconnect", e);
        } finally {
            this._isRefreshingToken = false;
        }
        this.connect(peerId, peerMetadata);
    }

    disconnect() {
        console.log("Disconnected sync server")
        super.disconnect()
    }

    // Cast to `() => void` to match the parent's type signature.
    // At runtime the WebSocket close event always passes a CloseEvent as the
    // first argument regardless of what TypeScript says, so this is safe.
    // We use `any` to avoid the ws vs. browser CloseEvent type conflict.
    onClose = ((event: any) => {
        const ts = new Date().toISOString();
        console.log(`[Automerge] ❌ Connection closed at ${ts}`, {
            code: event.code,
            reason: event.reason || "(no reason)",
            wasClean: event.wasClean,
            // Common codes:
            // 1000 Normal closure        1001 Going away
            // 1006 Abnormal (no frame)   1008 Policy violation (e.g. auth)
            // 1011 Server error          4xxx Application-specific
        });

        // Stop the keepalive — no point pinging a dead socket
        clearInterval(this._heartbeatInterval)
        this._heartbeatInterval = undefined

        if (this.remotePeerId) {
            this.emit("peer-disconnected", { peerId: this.remotePeerId })
        }

        if (!this.connectionIssueNoticed) {
            this.connectionIssueNoticed = true;
        }

        if (!this.timerId) {
            if (this.peerId) {
                this.connectWithFreshToken(this.peerId, this.peerMetadata!);
            }
        }
    }) as unknown as () => void

    onOpen = () => {
        const ts = new Date().toISOString();
        // console.log(`[Automerge] ✅ Connection opened at ${ts}`);

        clearInterval(this.timerId)
        this.timerId = undefined
        this.send(this.joinMessage(this.peerId!, this.peerMetadata!))

        // Keepalive: proxy/LB idle timeouts (e.g. AWS ALB = 60s) kill silent
        // WebSocket connections with code 1006. Send a ping every 30s so the
        // proxy always sees traffic before it cuts us off.
        clearInterval(this._heartbeatInterval)
        this._heartbeatInterval = setInterval(() => {
            if (this.socket?.readyState === WebSocket.OPEN && this.peerId) {
                // console.log(`[Automerge] 💓 keepalive ping at ${new Date().toISOString()}`)
                // The proxy only resets its idle timer on WebSocket data frames,
                // not on the server's native ping/pong control frames. We send
                // a "ping" type via the existing send() — it handles encoding.
                // The server's switch falls to the default case for unknown
                // types and forwards it as a generic "message" event; the Repo
                // ignores types it does not know about.
                this.send({ type: "ping", senderId: this.peerId } as any)
            }
        }, this.HEARTBEAT_MS)

        if (this.connectionIssueNoticed) {
            this.connectionIssueNoticed = false;
        }
    }


    joinMessage(senderId: PeerId,peerMetadata: PeerMetadata): JoinMessage {
        return {
            type: "join",
            senderId,
            peerMetadata,
            supportedProtocolVersions: ["1"],
        }
    }



}
